import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  TrendingUp, DollarSign, Activity, BookOpen, Heart, Leaf,
  BarChart3, Clock, Target, AlertCircle, CheckCircle2, PieChart,
  Briefcase, Award, TrendingDown, Users, Shield, Zap, Sparkles, ArrowRight
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const QUALITY_STYLES = {
  'Excellent Match': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Good Match':      'bg-sky-100 text-sky-800 border-sky-200',
  'Moderate Match':  'bg-amber-100 text-amber-800 border-amber-200',
  'Weak Match':      'bg-orange-100 text-orange-800 border-orange-200',
  'Poor Match':      'bg-rose-100 text-rose-800 border-rose-200',
};

const InvestorDashboard = () => {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, metricsRes, recRes] = await Promise.all([
        axios.get(`${API}/dashboard`, { headers }),
        axios.get(`${API}/investor-dashboard/metrics`, { headers }),
        axios.get(`${API}/blended-investments/recommended-for-me?limit=6`, { headers }).catch(() => ({ data: null })),
      ]);
      setDashboardData(dashRes.data);
      setMetrics(metricsRes.data);
      setRecommendations(recRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '€0';
    if (amount >= 1000000) return `€${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `€${(amount / 1000).toFixed(0)}K`;
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  const getCategoryColor = (cat) => {
    const map = { education: 'bg-blue-100 text-blue-800', care: 'bg-green-100 text-green-800', environment: 'bg-emerald-100 text-emerald-800' };
    return map[cat] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const map = { executing: 'bg-green-100 text-green-800', planning: 'bg-yellow-100 text-yellow-800', initiating: 'bg-blue-100 text-blue-800', closing: 'bg-purple-100 text-purple-800' };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const s = metrics?.summary || {};
  const sdg = metrics?.sdg_distribution || {};
  const capByType = metrics?.capital_by_type || {};
  const capTotal = Object.values(capByType).reduce((a, b) => a + b, 0) || 1;
  const instruments = metrics?.instruments || {};
  const instTotal = Object.values(instruments).reduce((a, b) => a + b, 0) || 1;

  const CAPITAL_COLORS = {
    public: '#3B82F6', private: '#10B981', philanthropic: '#8B5CF6',
    investment_fund: '#3B82F6', philanthropic_foundation: '#8B5CF6', other: '#6B7280',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Briefcase className="h-7 w-7 text-emerald-700" />
            {t('investor_dashboard.title')}
          </h1>
          <p className="text-gray-600 mt-1">{t('investor_dashboard.subtitle')}</p>
        </div>
        <Link to="/impact-dashboard">
          <Button variant="outline" data-testid="view-impact-dashboard">
            <BarChart3 className="h-4 w-4 mr-2" /> {t('investor_dashboard.view_impact_dashboard')}
          </Button>
        </Link>
      </div>

      {/* Key Financial Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { icon: DollarSign, label: t('investor_dashboard.metric_capital_available'), value: formatCurrency(s.total_capital_available), color: 'text-blue-600', bg: 'bg-blue-50' },
          { icon: TrendingUp, label: t('investor_dashboard.metric_capital_deployed'), value: formatCurrency(s.total_capital_deployed), color: 'text-emerald-700', bg: 'bg-emerald-50' },
          { icon: Target, label: t('investor_dashboard.metric_deployment_rate'), value: `${s.deployment_rate || 0}%`, color: 'text-green-700', bg: 'bg-green-50' },
          { icon: Zap, label: t('investor_dashboard.metric_avg_sroi'), value: s.avg_sroi > 0 ? `${s.avg_sroi}:1` : 'N/A', color: 'text-amber-600', bg: 'bg-amber-50' },
          { icon: Briefcase, label: t('investor_dashboard.metric_active_projects'), value: s.total_projects, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { icon: Users, label: t('investor_dashboard.metric_mentoring_hours'), value: t('investor_dashboard.mentoring_hours_value', { hours: s.total_mentoring_hours || 0 }), color: 'text-purple-600', bg: 'bg-purple-50' },
        ].map((m, i) => (
          <Card key={i} data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${m.bg}`}>
                  <m.icon className={`h-4 w-4 ${m.color}`} />
                </div>
                <div>
                  <p className={`text-lg font-bold ${m.color}`}>{m.value}</p>
                  <p className="text-xs text-gray-500">{m.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Plans matching your portfolio (Advanced Matching engine) */}
      <Card data-testid="recommendations-section" className="border-teal-200 bg-gradient-to-br from-teal-50/50 to-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-5 w-5 text-teal-700" />
              Plans matching your portfolio
              {recommendations?.candidate_pool > 0 && (
                <Badge variant="outline" className="ml-1 text-xs">
                  {recommendations.recommended?.length || 0} of {recommendations.candidate_pool}
                </Badge>
              )}
            </CardTitle>
            <Link to="/combined-investments">
              <Button variant="ghost" size="sm" data-testid="view-all-opportunities-btn">
                Browse all <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </Link>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Ranked using SDG alignment, sector fit, geographic focus, ticket size, ESG and non-financial support — matched against your declared investment criteria.
          </p>
        </CardHeader>
        <CardContent>
          {!recommendations || recommendations.recommended?.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No matching plans available right now.</p>
              <p className="text-xs text-gray-500 mt-1">
                {!recommendations?.investor_id
                  ? 'Complete your investor profile (SDG focus, sector & ticket size) to unlock personalised matches.'
                  : 'New plans will appear here as they are promoted.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recommendations.recommended.map((r) => (
                <Link
                  key={r.plan_id}
                  to={`/combined-investments/${r.plan_id}`}
                  className="block group"
                  data-testid={`recommendation-${r.plan_id}`}
                >
                  <div className="border rounded-lg p-3 hover:border-teal-400 hover:shadow-md transition-all bg-white h-full">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate group-hover:text-teal-700" title={r.title}>
                          {r.title}
                        </p>
                        {r.investment_title && (
                          <p className="text-xs text-gray-500 truncate" title={r.investment_title}>
                            {r.investment_title}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-xl font-bold text-teal-700" data-testid="rec-score">
                          {r.match_score}
                        </span>
                        <Badge className={`${QUALITY_STYLES[r.match_quality]} border text-xs`}>
                          {r.match_quality}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-1 text-xs mb-2">
                      <div>
                        <p className="text-gray-500">Budget</p>
                        <p className="font-medium text-gray-800">{formatCurrency(r.total_budget)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Status</p>
                        <Badge variant="outline" className="text-xs capitalize">{r.status?.replace('_', ' ')}</Badge>
                      </div>
                      <div>
                        <p className="text-gray-500">Segments</p>
                        <p className="font-medium text-gray-800">{r.segment_count}</p>
                      </div>
                    </div>

                    {r.matching_sdgs?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {r.matching_sdgs.slice(0, 4).map((s) => (
                          <Badge key={s} variant="outline" className="text-xs">SDG {s}</Badge>
                        ))}
                        {r.matching_sdgs.length > 4 && (
                          <span className="text-xs text-gray-500">+{r.matching_sdgs.length - 4}</span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SROI Visualization */}
        <Card data-testid="sroi-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="h-5 w-5 text-amber-700" /> {t('investor_dashboard.sroi_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {s.avg_sroi > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <div className="relative w-32 h-32">
                    <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                      <circle cx="60" cy="60" r="50" fill="none" stroke="#f3f4f6" strokeWidth="10" />
                      <circle cx="60" cy="60" r="50" fill="none" stroke="#f59e0b" strokeWidth="10"
                        strokeDasharray={`${Math.min(s.avg_sroi / 5 * 314, 314)} 314`}
                        strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-amber-600">{s.avg_sroi}</span>
                      <span className="text-xs text-gray-500">{t('investor_dashboard.sroi_ratio_caption')}</span>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-600">
                  {t('investor_dashboard.sroi_caption', { value: s.avg_sroi })}
                </p>
                {(metrics?.sroi_projects || []).map((sp) => (
                  <div key={sp.project_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">{sp.project_title}</span>
                    <Badge className="bg-amber-100 text-amber-800">{sp.sroi_ratio}:1</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Zap className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t('investor_dashboard.no_sroi')}</p>
                <p className="text-xs text-gray-500 mt-1">{t('investor_dashboard.no_sroi_hint')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SDG Impact Distribution */}
        <Card data-testid="sdg-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="h-5 w-5 text-green-700" /> {t('investor_dashboard.sdg_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(sdg).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(sdg).sort((a, b) => b[1] - a[1]).map(([name, count]) => {
                  const maxCount = Math.max(...Object.values(sdg));
                  const pct = Math.round((count / maxCount) * 100);
                  return (
                    <div key={name} className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 w-40 truncate">{name}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-4">
                        <div className="bg-green-700 h-4 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-gray-700 w-6 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Target className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t('investor_dashboard.no_sdg_data')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Capital Deployment by Type */}
        <Card data-testid="capital-type-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <PieChart className="h-5 w-5 text-blue-600" /> {t('investor_dashboard.capital_by_type_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(capByType).length > 0 ? (
              <div className="space-y-4">
                <div className="flex rounded-lg overflow-hidden h-6">
                  {Object.entries(capByType).map(([type, amount]) => {
                    const pct = Math.round((amount / capTotal) * 100);
                    const color = CAPITAL_COLORS[type] || '#6B7280';
                    return (
                      <div key={type} style={{ width: `${Math.max(pct, 5)}%`, backgroundColor: color }}
                        className="flex items-center justify-center text-white text-xs font-medium">
                        {pct > 10 ? `${pct}%` : ''}
                      </div>
                    );
                  })}
                </div>
                {Object.entries(capByType).map(([type, amount]) => {
                  const color = CAPITAL_COLORS[type] || '#6B7280';
                  return (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-sm capitalize text-gray-700">{type.replace(/_/g, ' ')}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-800">{formatCurrency(amount)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <PieChart className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t('investor_dashboard.no_capital_data')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Financial Instruments */}
        <Card data-testid="instruments-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-5 w-5 text-teal-700" /> {t('investor_dashboard.instruments_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(instruments).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(instruments).map(([name, amount]) => (
                  <div key={name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-700">{name}</span>
                      <span className="text-sm font-medium text-teal-700">{formatCurrency(amount)}</span>
                    </div>
                    <div className="bg-gray-100 rounded-full h-2">
                      <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${(amount / instTotal) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t('investor_dashboard.no_instruments')}</p>
                <p className="text-xs text-gray-500 mt-1">{t('investor_dashboard.no_instruments_hint')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Project Performance Table */}
      <Card data-testid="project-performance-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-5 w-5 text-indigo-600" /> {t('investor_dashboard.project_performance_title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(metrics?.project_performance || []).length > 0 ? (
            <div className="space-y-3">
              {metrics.project_performance.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1 min-w-0">
                    <Link to={`/projects/${p.id}`} className="font-medium text-gray-900 hover:text-blue-600">
                      {p.title}
                    </Link>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={getCategoryColor(p.category)}>{p.category}</Badge>
                      <Badge className={getStatusColor(p.status)}>{p.status}</Badge>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-6 text-sm">
                    <div className="text-center w-20">
                      <p className="font-medium text-gray-800">{formatCurrency(p.budget)}</p>
                      <p className="text-xs text-gray-500">{t('investor_dashboard.label_budget')}</p>
                    </div>
                    <div className="text-center w-20">
                      <p className="font-medium text-gray-800">{p.budget_utilization}%</p>
                      <p className="text-xs text-gray-500">{t('investor_dashboard.label_utilized')}</p>
                    </div>
                    <div className="w-20">
                      <div className="flex items-center gap-1 mb-1">
                        <span className="text-xs font-medium">{p.progress}%</span>
                        {p.progress >= 75 ? <CheckCircle2 className="h-3 w-3 text-green-500" /> : <Clock className="h-3 w-3 text-amber-700" />}
                      </div>
                      <Progress value={p.progress} className="h-1.5" />
                    </div>
                    <div className="text-center w-16">
                      {p.sroi_ratio ? (
                        <Badge className="bg-amber-100 text-amber-800">{p.sroi_ratio}:1</Badge>
                      ) : (
                        <span className="text-xs text-gray-500">—</span>
                      )}
                      <p className="text-xs text-gray-500 mt-0.5">{t('investor_dashboard.label_sroi')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Briefcase className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">{t('investor_dashboard.no_project_data')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Investor Profiles */}
      <Card data-testid="investor-profiles-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-5 w-5 text-blue-600" /> {t('investor_dashboard.investor_profiles_title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(metrics?.investor_profiles || []).map((inv) => (
              <div key={inv.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">{inv.organization_name}</h4>
                  <Badge variant="outline" className="text-xs capitalize">{(inv.investor_type || '').replace(/_/g, ' ')}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">{t('investor_dashboard.label_available')}</p>
                    <p className="font-medium text-blue-700">{formatCurrency(inv.capital_available)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">{t('investor_dashboard.label_deployed')}</p>
                    <p className="font-medium text-emerald-700">{formatCurrency(inv.total_deployed)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">{t('investor_dashboard.label_risk_appetite')}</p>
                    <p className="font-medium capitalize">{inv.risk_appetite || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">{t('investor_dashboard.label_certification')}</p>
                    <p className="font-medium capitalize">{(inv.certification_level || 'none').replace(/_/g, ' ')}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(inv.sdg_alignment || []).map((sdg) => (
                    <Badge key={sdg} className="bg-green-50 text-green-700 text-xs">SDG {sdg}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card data-testid="investor-quick-actions">
        <CardHeader><CardTitle>{t('investor_dashboard.investment_management')}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Link to="/projects"><Button className="w-full justify-start h-auto p-4" variant="outline"><Activity className="h-5 w-5 mr-3" /><div className="text-left"><p className="font-medium">{t('investor_dashboard.view_projects')}</p><p className="text-sm text-gray-500">{t('investor_dashboard.view_projects_desc')}</p></div></Button></Link>
            <Link to="/investor/insights" data-testid="investor-insights-cta"><Button className="w-full justify-start h-auto p-4" variant="outline"><Sparkles className="h-5 w-5 mr-3 text-indigo-600" /><div className="text-left"><p className="font-medium">Investor Insights</p><p className="text-sm text-gray-500">Read-only pipeline of Participatory-Design ideas you may back.</p></div></Button></Link>
            <Link to="/impact-dashboard"><Button className="w-full justify-start h-auto p-4" variant="outline"><BarChart3 className="h-5 w-5 mr-3" /><div className="text-left"><p className="font-medium">{t('investor_dashboard.view_impact_dashboard')}</p><p className="text-sm text-gray-500">{t('investor_dashboard.impact_dashboard_desc')}</p></div></Button></Link>
            <Link to="/combined-investments"><Button className="w-full justify-start h-auto p-4" variant="outline"><PieChart className="h-5 w-5 mr-3" /><div className="text-left"><p className="font-medium">{t('investor_dashboard.blended_plans')}</p><p className="text-sm text-gray-500">{t('investor_dashboard.blended_plans_desc')}</p></div></Button></Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestorDashboard;
